import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { logger } from '@/lib/logger';
import { useSessionValidator } from '@/hooks/useSessionValidator';

interface OnboardingGuardProps {
  children: React.ReactNode;
  requireOnboardingComplete?: boolean;
}

/**
 * OnboardingGuard - Enforces onboarding completion before accessing protected features
 *
 * Security layer that prevents users from:
 * - Manually typing URLs to bypass onboarding
 * - Using bookmarked links to skip setup
 * - Accessing features without completing mandatory steps (tier, payment, consent)
 * - Accessing protected routes with expired sessions
 *
 * @param requireOnboardingComplete - If true, redirects to /onboarding if incomplete (default: true)
 *
 * @example
 * ```tsx
 * // Protect dashboard - requires onboarding completion
 * <OnboardingGuard requireOnboardingComplete>
 *   <Dashboard />
 * </OnboardingGuard>
 *
 * // Onboarding page itself - skip onboarding check
 * <OnboardingGuard requireOnboardingComplete={false}>
 *   <OnboardingWizard />
 * </OnboardingGuard>
 * ```
 */
export const OnboardingGuard: React.FC<OnboardingGuardProps> = ({
  children,
  requireOnboardingComplete = true
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // PERF DIAGNOSTIC: Track when OnboardingGuard renders
  React.useEffect(() => {
    console.log(`🛡️ [PERF] OnboardingGuard rendered at ${new Date().toISOString().split('T')[1]} (loading: ${loading}, user: ${!!user})`);
  }, [loading, user]);

  // Validate session on mount (will redirect to /auth if expired)
  useSessionValidator();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <div className="text-lg">Vérification...</div>
        </div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    logger.debug('OnboardingGuard: No user, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Check onboarding completion status
  const onboardingCompleted = user.user_metadata?.onboarding_completed;

  // Also check if user has selected a valid tier (backward compatibility check)
  const productTier = user.user_metadata?.product_tier;
  const hasValidTier = productTier && (
    productTier === 'pilotage-unique' ||
    productTier === 'pilotage-collectif' ||
    productTier === 'entrepreneur' || // Legacy support
    productTier === 'consultant' // Legacy support
  );

  // If onboarding is required but not completed, redirect to onboarding
  if (requireOnboardingComplete && !onboardingCompleted) {
    logger.warn('OnboardingGuard: Onboarding incomplete, redirecting to /onboarding', {
      attempted_route: location.pathname,
      user_id: user.id,
      user_metadata: user.user_metadata,
      has_valid_tier: hasValidTier
    });

    // Store intended destination to redirect after onboarding completion
    // This allows "View this subsidy" email links to work correctly
    const returnTo = location.pathname !== '/onboarding' ? location.pathname : undefined;

    return (
      <Navigate
        to="/onboarding"
        replace
        state={{ returnTo }}
      />
    );
  }

  // If onboarding is NOT required (e.g., onboarding page itself), allow access
  // OR if onboarding is completed, allow access
  logger.debug('OnboardingGuard: Access granted', {
    pathname: location.pathname,
    onboarding_completed: onboardingCompleted,
    require_onboarding: requireOnboardingComplete
  });

  return <>{children}</>;
};
